import { ColumnsProps } from '@/Helper';
import { SelectOptions } from '@/model/CommonModel';
import { ContextMenuContent, EventViewerActions, EventViewerContextOptions } from '@/model/EventModel';
import React, { CSSProperties, useEffect, useRef } from 'react';
import styles from './virtualTable.module.scss';

export const selectedStyle: CSSProperties = { backgroundColor: '#d9d9d9', color: 'black' };

type RowProps<T> = {
	data: T[];
	index: number;
	columnsWidth: CSSProperties;
	contextMenuOptions: EventViewerContextOptions<T>;
	windowWidth: number;
	backgroundColor: CSSProperties;
	columns: ColumnsProps<T>[];
	selectedRowKey: string;
	setSelectedRowKey: (key: string) => void;
	setSize: (index: number, size: number) => void;
	setContextMenuContent: (content: ContextMenuContent<T>) => void;
};

const component = <T extends {}>({
	data,
	index,
	selectedRowKey,
	setSelectedRowKey,
	setSize,
	setContextMenuContent,
	windowWidth,
	columns,
	columnsWidth,
	contextMenuOptions,
	backgroundColor,
}: RowProps<T>): JSX.Element => {
	//#region State Management Variables
	const rowRef = useRef<HTMLDivElement>(undefined);
	//#endregion

	useEffect(() => {
		const { current } = rowRef;
		if (current) {
			setSize(index, current.getBoundingClientRect().height);
		}
	}, [setSize, index, windowWidth]);

	let modifiedContextMenuOptions: SelectOptions<EventViewerActions>[] = contextMenuOptions.options.map(option => ({ ...option }));
	if (!modifiedContextMenuOptions.find(option => option.value === EventViewerActions.DisplayCredential).disabled) {
		const disabled = data[index]['UID1'] === 0 && data[index]['UID2'] === 0;
		modifiedContextMenuOptions.find(option => option.value === EventViewerActions.DisplayCredential).disabled = disabled;
	}

	if (data[index]['MatchCode'] && data[index]['MatchCode'] !== '') {
		const matchOption: SelectOptions<EventViewerActions> = modifiedContextMenuOptions.find(option => option.value === EventViewerActions.CopyMatchCode);
		if (matchOption) {
			matchOption.disabled = false;
		}
	}

	const isSelected = selectedRowKey === data[index]['key'];
	const cellStyle = isSelected ? selectedStyle : backgroundColor;
	data[index]['selected'] = isSelected;

	const onMouseUpHandled = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		if (event.button === 2) {
			setContextMenuContent({ options: [], record: data[index], visible: true });
			setSelectedRowKey(data[index]['key']);
			setTimeout(() => {
				setContextMenuContent({ options: modifiedContextMenuOptions, record: data[index], visible: true });
			}, 400);
		}
	};

	const row: JSX.Element = (
		<div ref={rowRef} style={columnsWidth} className={styles.row} onMouseUp={onMouseUpHandled}>
			{columns.map((column: ColumnsProps<T>, columnIndex: number) => (
				<div key={`${index}-${columnIndex}`} className={styles.cell} style={cellStyle}>
					{column.render(data[index][column.dataIndex], data[index], columnIndex) as React.ReactNode}
				</div>
			))}
		</div>
	);

	return row;
};

const Row = React.memo(component) as <T>(props: RowProps<T>) => React.ReactNode;

export { Row };
